import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Plus, Save, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useModuleAcl } from '@/hooks/useModuleAcl';
import { postApiCall } from '@/service/httpCall';
import { API_CONTACT_CUSTOM_FIELD_TEMPLATES } from '@/service/url';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { MODULE_ACL, ROUTES } from '@/utilities/constant';

import {
  BASE_CONTACT_FIELD_COUNT,
  FIELD_NAME_REGEX,
  FIELD_TYPES,
  MAX_CONTACT_FORM_FIELDS,
  MAX_CUSTOM_FIELDS,
  MAX_NAME_LEN,
  TEMPLATE_NAME_REGEX,
  TYPE_LABELS,
  builderToPayload,
  emptyBuilderField,
  templateFieldToBuilder,
  type BuilderField,
  type CustomFieldType,
} from './customFields';
import { FieldSettingsRow, RequiredCheckbox } from './FieldSettings';
import { useContactCustomFieldsAcl, useCustomFieldTemplates } from './useCustomFieldTemplates';

const inputCls = 'input-embossed h-9 w-full rounded-md px-2.5 text-sm disabled:opacity-50';
const listPath = `/${ROUTES.CONTACTGROUP.CUSTOM_FIELD_TEMPLATES}`;

export default function CustomFieldTemplateFormPage() {
  const navigate = useNavigate();
  const enabled = useContactCustomFieldsAcl();
  const { canAdd } = useModuleAcl(MODULE_ACL.CONTACTS);
  const allowed = enabled && canAdd;

  const [name, setName] = useState('');
  const [fields, setFields] = useState<BuilderField[]>([emptyBuilderField()]);
  const [saving, setSaving] = useState(false);
  const { templates: cloneSources, loading: cloneSourcesLoading } = useCustomFieldTemplates();
  const [cloneFromId, setCloneFromId] = useState('');

  // Copy another template's fields into the builder, then edit freely. "New fields" clears them
  // back to one empty field.
  const cloneFrom = (value: string) => {
    setCloneFromId(value);
    const source = cloneSources.find((t) => String(t.id) === value);
    if (!source) {
      setFields([emptyBuilderField()]);
      return;
    }
    setFields(source.fields.length ? source.fields.map(templateFieldToBuilder) : [emptyBuilderField()]);
    toast.success(`Copied ${source.fields.length} field(s) from "${source.name}"`);
  };

  const updateField = <K extends keyof BuilderField>(idx: number, key: K, value: BuilderField[K]) => {
    setFields((prev) =>
      prev.map((f, i) => {
        if (i !== idx) return f;
        if (key === 'field_type') {
          // A new type starts with a clean default/min/max/options.
          return { ...emptyBuilderField(), field_name: f.field_name, is_required: f.is_required, field_type: value as CustomFieldType };
        }
        return { ...f, [key]: value };
      }),
    );
  };

  const addField = () => {
    if (fields.length >= MAX_CUSTOM_FIELDS) return;
    setFields((prev) => [...prev, emptyBuilderField()]);
  };
  const removeField = (idx: number) => setFields((prev) => prev.filter((_, i) => i !== idx));

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error('Template name is required');
      return;
    }
    const built = builderToPayload(fields);
    if ('error' in built) {
      toast.error(built.error);
      return;
    }
    setSaving(true);
    postApiCall(API_CONTACT_CUSTOM_FIELD_TEMPLATES, { name: trimmed, fields: built.fields })
      .then(() => {
        toast.success('Template created');
        navigate(listPath);
      })
      .catch((err: unknown) => toast.error(friendlyApiError(err) || 'Failed to save template'))
      .finally(() => setSaving(false));
  };

  if (!allowed) {
    return (
      <ScrollPage>
        <PageHeader title="Create Custom Field Template" />
        <div className="rounded-md border border-border bg-muted/30 p-8 text-center text-sm text-muted-foreground">
          Custom fields are not enabled in your assigned ACL (Contacts → Options → Custom fields).
        </div>
      </ScrollPage>
    );
  }

  const total = BASE_CONTACT_FIELD_COUNT + fields.length;

  return (
    <ScrollPage>
      <PageHeader
        title="Create Custom Field Template"
        description="These fields show on the contact form after the standard fields when this template is selected, and as extra columns in the upload template."
        actions={
          <Button variant="outline" onClick={() => navigate(listPath)}>
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
        }
      />

      <div className="rounded-2xl border border-border/60 bg-card p-4 shadow-neu-raised">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-1.5">
          <label htmlFor="template-name" className="text-xs font-semibold text-foreground">
            Template Name <span className="text-destructive">*</span>
          </label>
          <input
            id="template-name"
            value={name}
            maxLength={MAX_NAME_LEN}
            onChange={(e) => {
              if (TEMPLATE_NAME_REGEX.test(e.target.value)) setName(e.target.value);
            }}
            placeholder="e.g. Loan Leads"
            className={inputCls}
          />
          <p className="text-[11px] text-muted-foreground">Letters, numbers, spaces, _ and -</p>
        </div>

        <div className="space-y-1.5">
          <label htmlFor="clone-from-template" className="text-xs font-semibold text-foreground">
            Copy fields from
          </label>
          <select
            id="clone-from-template"
            value={cloneFromId}
            disabled={cloneSourcesLoading || !cloneSources.length}
            onChange={(e) => cloneFrom(e.target.value)}
            className={inputCls}
          >
            <option value="">
              {cloneSourcesLoading ? 'Loading templates…' : cloneSources.length ? 'New fields (add your own)' : 'No templates to copy from'}
            </option>
            {cloneSources.map((t) => (
              <option key={t.id} value={String(t.id)}>
                {t.name} ({t.fields.length} field{t.fields.length === 1 ? '' : 's'})
              </option>
            ))}
          </select>
          <p className="text-[11px] text-muted-foreground">
            Pick a saved template to copy its fields below, then change them as you like. That template stays as it is. Choosing New fields clears the copied fields.
          </p>
        </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-border/50 pt-3">
          <div>
            <h5 className="text-sm font-bold text-foreground">Fields</h5>
            <p className="text-[11px] text-muted-foreground">
              {fields.length} / {MAX_CUSTOM_FIELDS} custom fields · {total} / {MAX_CONTACT_FORM_FIELDS} fields on the contact form
              (required or not, every field counts)
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={addField}
            disabled={fields.length >= MAX_CUSTOM_FIELDS}
            className="gap-1.5"
            title={fields.length >= MAX_CUSTOM_FIELDS ? `Limit of ${MAX_CUSTOM_FIELDS} custom fields reached` : undefined}
          >
            <Plus className="h-3.5 w-3.5" />
            Add Field
          </Button>
        </div>

        {fields.map((field, idx) => {
          const type = field.field_type;
          return (
            <div key={idx} className="mt-3 rounded-xl border border-border/60 bg-muted/20 p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary ring-1 ring-inset ring-primary/25">
                  Field #{idx + 1}
                </span>
                {fields.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeField(idx)}
                    title="Remove field"
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-rose-100 text-rose-700 ring-1 ring-inset ring-rose-200 transition hover:bg-rose-200 dark:bg-rose-950/40 dark:text-rose-300"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-12">
                <div className="space-y-1.5 md:col-span-5">
                  <label className="text-xs font-semibold text-foreground">
                    Field Name <span className="text-destructive">*</span>
                  </label>
                  <input
                    value={field.field_name}
                    maxLength={MAX_NAME_LEN}
                    onChange={(e) => {
                      if (FIELD_NAME_REGEX.test(e.target.value)) updateField(idx, 'field_name', e.target.value);
                    }}
                    placeholder="e.g. Customer Name"
                    className={inputCls}
                  />
                </div>

                <div className="space-y-1.5 md:col-span-4">
                  <label className="text-xs font-semibold text-foreground">Type</label>
                  <select
                    value={type}
                    onChange={(e) => updateField(idx, 'field_type', e.target.value as CustomFieldType)}
                    className={inputCls}
                  >
                    {FIELD_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {TYPE_LABELS[t]}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-end md:col-span-3">
                  <RequiredCheckbox field={field} onChange={(key, value) => updateField(idx, key, value)} />
                </div>
              </div>

              <FieldSettingsRow field={field} onChange={(key, value) => updateField(idx, key, value)} />
            </div>
          );
        })}

        <div className="mt-4 flex justify-end gap-2 border-t border-border/50 pt-3">
          <Button variant="outline" onClick={() => navigate(listPath)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Create Template
          </Button>
        </div>
      </div>
    </ScrollPage>
  );
}
